import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Empty, Input, Modal, Popconfirm, Space, Table, Tag, Typography, message } from "antd";
import type { InputRef } from "antd";
import {
	CopyOutlined,
	DeleteOutlined,
	ExclamationCircleFilled,
	SearchOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import {
	cloneBlockSchedule,
	deleteBlockSchedule,
	loadBlockSchedules,
	saveBlockScheduleTitle,
	touchBlockSchedule,
} from "../../utils";
import { getEmailFromSessionStorage } from "../../utils/session_utils";
import CloneScheduleForm from "./CloneScheduleForm";

export function generateCloneName(originalTitle: string, existingTitles: string[]): string {
	const baseName = `${originalTitle} clone`;
	if (!existingTitles.includes(baseName)) {
		return baseName;
	}
	const escapedBase = baseName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
	const numberedPattern = new RegExp(`^${escapedBase} (\\d+)$`);
	let maxN = -1;
	for (const title of existingTitles) {
		const match = title.match(numberedPattern);
		if (match) {
			maxN = Math.max(maxN, parseInt(match[1], 10));
		}
	}
	return maxN === -1 ? `${baseName} 1` : `${baseName} ${maxN + 1}`;
}

export type SearchableSchedule = {
	title: string;
	created_by?: string;
	last_modified_by?: string;
};

/**
 * Filters saved schedules by name, owner, or last modifier. Case-insensitive
 * and order-independent: every whitespace-separated token must appear in at
 * least one of those three fields, so "june amagi" finds "Amagi FAST — June"
 * and "amagi dilshad" narrows that to the ones dilshad owns or last touched.
 * A blank query returns the list as-is.
 */
export function filterSchedulesByName<T extends SearchableSchedule>(
	schedules: T[],
	query: string
): T[] {
	const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
	if (tokens.length === 0) return schedules;
	return schedules.filter(schedule => {
		// One combined haystack, so a name token and a person token can narrow together.
		const haystack = [schedule.title, schedule.created_by, schedule.last_modified_by]
			.filter(Boolean)
			.join(" ")
			.toLowerCase();
		return tokens.every(token => haystack.includes(token));
	});
}

/**
 * `updated_at` is stamped when a schedule is created and bumped on every change,
 * so it already carries whichever of the two is more recent — there is no
 * separate created_at column to fall back on.
 */
export function formatLastUpdated(timestamp?: string): string {
	const parsed = dayjs(timestamp);
	// Anything missing or unparseable shows as an em dash rather than a mangled
	// fragment of the raw value.
	return timestamp && parsed.isValid() ? parsed.format("YYYY-MM-DD") : "—";
}

/** "dilshad@mvmediasales.com" → "dilshad" — the domain is noise in a table. */
export function emailLocalPart(email: string): string {
	return email.split("@")[0] || email;
}

// Renders an email cell as a "You" tag for the current user, the shortened
// address for anyone else (full address on hover), and an em dash when the
// column was never stamped.
const UserCell = ({ email, currentUserEmail }: { email?: string; currentUserEmail: string }) =>
	email ? (
		email === currentUserEmail ? (
			<Tag color="green">You</Tag>
		) : (
			<Typography.Text title={email}>{emailLocalPart(email)}</Typography.Text>
		)
	) : (
		<Typography.Text type="secondary">—</Typography.Text>
	);

type BlockSchedulePickerModalProps = {
	visible: boolean;
	currentSelectedScheduleId: string;
	onClose: (data: any | null) => void;
	/** Fired after a delete, so the calendar can react when the open schedule is the one removed. */
	onDeleted?: (deletedScheduleId: string) => void;
};
import "./BlockSchedulePickerModal.css";
const BlockSchedulePickerModal = ({
	visible,
	currentSelectedScheduleId,
	onClose,
	onDeleted,
}: BlockSchedulePickerModalProps) => {
	const [loading, setLoading] = useState(false);
	const [schedules, setSchedules] = useState<any[]>([]);
	const [cloningId, setCloningId] = useState<string | null>(null);
	const [searchQuery, setSearchQuery] = useState<string>("");
	const [deletingId, setDeletingId] = useState<string | null>(null);
	const [isCloning, setIsCloning] = useState<boolean>(false);
	const [cloneSource, setCloneSource] = useState<any | null>(null);
	const [cloneNameInput, setCloneNameInput] = useState<string>("");
	const [cloneNewStart, setCloneNewStart] = useState<Dayjs | null>(null);
	const [cloneNewEnd, setCloneNewEnd] = useState<Dayjs | null>(null);
	const [bringForward, setBringForward] = useState<boolean>(true);
	const currentUserEmail = getEmailFromSessionStorage() || "";

	const resetCloneDialog = () => {
		setCloneSource(null);
		setCloneNewStart(null);
		setCloneNewEnd(null);
		setBringForward(true);
	};

	useEffect(() => {
		if (visible) {
			setSearchQuery("");
			loadBlockSchedules(
				(data: any[]) => {
					// Surface anything this user has a stake in first — schedules they
					// last worked on OR created — while still listing everyone's.
					// updated_at DESC order is preserved within each group.
					const isMine = (s: any) =>
						s.last_modified_by === currentUserEmail || s.created_by === currentUserEmail;
					const own = data.filter((s: any) => !!currentUserEmail && isMine(s));
					const others = data.filter((s: any) => !currentUserEmail || !isMine(s));
					setSchedules([...own, ...others]);
					setLoading(false);
				},
				(error: any) => {
					message.error("Failed to load block schedules");
					setLoading(false);
				}
			);
		}
	}, [visible]);

	const filteredSchedules = useMemo(
		() => filterSchedulesByName(schedules, searchQuery),
		[schedules, searchQuery]
	);

	// Deleting a character widens the filter, so rows MOUNT — and each mounted
	// editable title cell focuses its own edit icon (antd's guard against that is
	// defeated by StrictMode's double-invoked effects). Typing only unmounts rows,
	// which is why the caret escapes on delete alone. Take it back a frame later,
	// once every effect pass has settled, and only on a render caused by typing.
	const searchInputRef = useRef<InputRef>(null);
	const pendingFocusRef = useRef<number | null>(null);

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		pendingFocusRef.current = event.target.selectionStart;
		setSearchQuery(event.target.value);
	};

	useEffect(() => {
		const caretPosition = pendingFocusRef.current;
		if (caretPosition === null) return;
		pendingFocusRef.current = null;
		const frame = requestAnimationFrame(() => {
			const inputElement = searchInputRef.current?.input;
			if (!inputElement || document.activeElement === inputElement) return;
			inputElement.focus();
			const caret = Math.min(caretPosition, inputElement.value.length);
			inputElement.setSelectionRange(caret, caret);
		});
		return () => cancelAnimationFrame(frame);
	}, [filteredSchedules]);

	const handleSelect = (schedule: any) => {
		// Record this as the schedule the current user last used, so their next page
		// load reopens it. It is stored against the user, not the schedule, so it
		// neither claims they modified it nor displaces it from a teammate's own
		// last-used. Not awaited: a slow metadata write must not delay the modal
		// from closing.
		if (schedule?.id && currentUserEmail) {
			void touchBlockSchedule(schedule.id);
		}
		onClose(schedule);
	};

	const handleCancel = () => {
		onClose(null);
	};

	const handleDelete = async (record: any) => {
		setDeletingId(record.id);
		await deleteBlockSchedule(
			record.id,
			() => {
				setSchedules((prev: any[]) => prev.filter((s: any) => s.id !== record.id));
				message.success(`Deleted "${record.title}"`);
				setDeletingId(null);
				onDeleted?.(record.id);
			},
			(error: any) => {
				// 404 means a teammate already deleted it — drop the stale row anyway.
				if (error?.error?.response?.status === 404) {
					setSchedules((prev: any[]) => prev.filter((s: any) => s.id !== record.id));
					message.warning(`"${record.title}" was already deleted`);
					setDeletingId(null);
					onDeleted?.(record.id);
					return;
				}
				message.error(`Failed to delete "${record.title}"`);
				setDeletingId(null);
			}
		);
	};

	const openCloneDialog = (record: any) => {
		const existingTitles = schedules.map((s: any) => s.title);
		const suggestedName = generateCloneName(record.title, existingTitles);
		setCloneSource(record);
		setCloneNameInput(suggestedName);
	};

	const handleCloneConfirm = async () => {
		if (!cloneSource) return;
		const newTitle = cloneNameInput.trim();
		if (!newTitle) {
			message.warning("Please enter a name for the cloned schedule.");
			return;
		}
		const source = cloneSource;
		const newStartDate = cloneNewStart?.format('YYYY-MM-DD');
		const newEndDate   = cloneNewEnd?.format('YYYY-MM-DD');
		setIsCloning(true);
		setCloningId(source.id);
		await cloneBlockSchedule(
			source.id,
			newTitle,
			(result) => {
				const clonedSchedule = {
					...source,
					id: result.blockScheduleId,
					title: newTitle,
					start_time: newStartDate ?? source.start_time,
					end_time:   newEndDate   ?? source.end_time,
					updated_at: new Date().toISOString(),
				};
				setSchedules((prev: any[]) => [clonedSchedule, ...prev]);
				const dateRange = newStartDate && newEndDate
					? ` with content shifted to ${newStartDate} → ${newEndDate}`
					: '';
				message.success(`Cloned as "${newTitle}"${dateRange}`);
				setIsCloning(false);
				setCloningId(null);
				resetCloneDialog();
			},
			(_error) => {
				message.error("Failed to clone schedule");
				setIsCloning(false);
				setCloningId(null);
			},
			{ newStartDate, newEndDate, bringForward }
		);
	};

	return (
		<>
			<Modal
				open={visible}
				title="Select a Block Schedule"
				onCancel={handleCancel}
				footer={null}
				width="75vw"
			>
				<Space direction="horizontal" align="center" style={{ marginTop: "16px" }}>
					<Input
						ref={searchInputRef}
						allowClear
						prefix={<SearchOutlined />}
						placeholder="Search by name, owner, or last modified by"
						value={searchQuery}
						onChange={handleSearchChange}
						style={{ width: 380 }}
						aria-label="Search schedules by name, owner, or last modified by"
					/>
					<Typography.Text type="secondary">
						{searchQuery.trim()
							? `${filteredSchedules.length} of ${schedules.length} schedules`
							: `${schedules.length} schedules`}
					</Typography.Text>
				</Space>
				{loading ? (
					<Typography.Text>Loading block schedules...</Typography.Text>
				) : (
					<Table
						dataSource={filteredSchedules}
						locale={{
							emptyText: (
								<Empty
									image={Empty.PRESENTED_IMAGE_SIMPLE}
									description={
										searchQuery.trim()
											? `No schedules match "${searchQuery.trim()}"`
											: "No saved schedules yet"
									}
								/>
							),
						}}
						loading={loading}
						rowClassName={record =>
							record.id === currentSelectedScheduleId ? "highlighted-row" : ""
						}
						columns={[
							{
								title: "Title",
								dataIndex: "title",
								key: "title",
								width: "200px",
								render: (text: string, record: any) => (
									<Typography.Paragraph
										editable={{
											onChange: async newTitle => {
												try {
													// Assuming saveBlockSchedule is a function to save the updated schedule
													setLoading(true);
													await saveBlockScheduleTitle(
														{ ...record, title: newTitle },
														() => {
															message.success("Title updated successfully");
															setLoading(false);
															handleSelect({ ...record, title: newTitle });
														},
														error => {
															message.error("Failed to update title");
															setLoading(false);
														}
													);
													setSchedules(prevSchedules =>
														prevSchedules.map(schedule =>
															schedule.id === record.id
																? { ...schedule, title: newTitle }
																: schedule
														)
													);
												} catch (error) {
													message.error("Failed to update title");
												}
											},
										}}
										style={{ marginBottom: 0 }}
									>
										{text}
									</Typography.Paragraph>
								),
							},
							{
								title: "Last Updated",
								dataIndex: "updated_at",
								key: "updated_at",
								width: "130px",
								render: (text: string) => (
									<Typography.Text title={text || undefined}>
										{formatLastUpdated(text)}
									</Typography.Text>
								),
							},
							{
								title: "Start Date",
								dataIndex: "start_time",
								key: "start_time",
								width: "130px",
								render: (text: string) => {
									return text.split(" ")[0];
								},
							},
							{
								title: "End Date",
								dataIndex: "end_time",
								key: "end_time",
								width: "130px",
								render: (text: string) => {
									return text.split(" ")[0];
								},
							},
							{
								title: "Distribution Partners",
								dataIndex: "platform",
								key: "platform",
								width: "250px",
								render: (text: string, record: any) => {
									return record.platform?.split(",").map((platform: string, index: number) => (
										<Tag key={index} color="blue">
											{platform}
										</Tag>
									));
								},
							},
							{
								title: "CloudPort Segments Feed",
								dataIndex: "channel",
								key: "channel",
								width: "210px",
							},
							{
								title: "Owner",
								dataIndex: "created_by",
								key: "created_by",
								width: "180px",
								render: (text: string) => (
									<UserCell email={text} currentUserEmail={currentUserEmail} />
								),
							},
							// {
							// 	title: "Last Modified By",
							// 	dataIndex: "last_modified_by",
							// 	key: "last_modified_by",
							// 	width: "180px",
							// 	render: (text: string) => (
							// 		<UserCell email={text} currentUserEmail={currentUserEmail} />
							// 	),
							// },
							{
								title: "Actions",
								key: "actions",
								width: "220px",
								render: (_, record) => (
									<Space size="middle">
										<Button type="link" onClick={() => handleSelect(record)} style={{ padding: 0 }}>
											Select
										</Button>
										<Button
											type="link"
											icon={<CopyOutlined />}
											loading={cloningId === record.id}
											onClick={() => openCloneDialog(record)}
											style={{ padding: 0 }}
										>
											Clone
										</Button>
										<Popconfirm
											title={`Delete "${record.title}"?`}
											description={
												<div style={{ maxWidth: 280 }}>
													This permanently removes the saved schedule for everyone. It cannot
													be undone.
													{record.id === currentSelectedScheduleId && (
														<div>This is the schedule you currently have open.</div>
													)}
												</div>
											}
											icon={<ExclamationCircleFilled style={{ color: "#ff4d4f" }} />}
											okText="Delete"
											cancelText="Cancel"
											okButtonProps={{ danger: true, loading: deletingId === record.id }}
											onConfirm={() => handleDelete(record)}
										>
											<Button
												type="link"
												danger
												// The class is what actually paints it red: a global
												// `.ant-btn-link { color: … !important }` in index.less
												// outranks both `danger` and any inline style.
												className="block-schedule-delete-button"
												icon={<DeleteOutlined />}
												loading={deletingId === record.id}
												// One delete at a time — a half-finished list is hard to reason about.
												disabled={!!deletingId && deletingId !== record.id}
												style={{ padding: 0 }}
												aria-label={`Delete ${record.title}`}
											>
												Delete
											</Button>
												
										</Popconfirm>
									</Space>
								),
							},
						]}
						rowKey="id"
						pagination={false}
						scroll={{ y: "64vh" }}
						style={{ marginTop: "20px" }}
					/>
				)}
			</Modal>

			<Modal
				title="Clone Schedule"
				open={!!cloneSource}
				onCancel={() => {
					if (!isCloning) resetCloneDialog();
				}}
				onOk={handleCloneConfirm}
				okText="Clone"
				okButtonProps={{
					loading: isCloning,
					disabled:
						!cloneNewStart ||
						!cloneNewEnd ||
						(!!cloneNewStart && !!cloneNewEnd && !cloneNewEnd.isAfter(cloneNewStart)),
				}}
				cancelButtonProps={{ disabled: isCloning }}
				closable={!isCloning}
				maskClosable={!isCloning}
				width={520}
			>
				<CloneScheduleForm
					cloneSource={cloneSource}
					cloneNameInput={cloneNameInput}
					setCloneNameInput={setCloneNameInput}
					cloneNewStart={cloneNewStart}
					setCloneNewStart={setCloneNewStart}
					cloneNewEnd={cloneNewEnd}
					setCloneNewEnd={setCloneNewEnd}
					bringForward={bringForward}
					setBringForward={setBringForward}
					onPressEnter={handleCloneConfirm}
				/>
			</Modal>
		</>
	);
};

export default BlockSchedulePickerModal;
